import _ from 'lodash';
import crypto from 'crypto';

import {
  ExperimentListActionTypes,
  VALIDATING_EXPERIMENTS_FILE,
  EXPERIMENTS_FILE_VALIDATION_FAILED,
  EXPERIMENTS_FILE_VALIDATION_SUCCEEDED,
  SELECTED_EXPERIMENTS_FILE,
  ISelectedExperimentsFileAction,
  IExperimentListsState,
  DISCARD_EXPERIMENT_LIST,
  START_UPLOAD,
  IActionString,
  UPLOADS_LOCAL_CHANGE,
} from '@store/types';

import {
  validateExperimentsFile,
  createUpload as startUploadRequest,
} from '@services/upload';
import { IExperimentResult } from '@models';
import { getUploads } from '@store/actions';
import { IApplicationState } from '@store/reducer';

const hashingAlgorithm = 'sha256';

export const selectExperimentsFile = (
  id: string,
  file: File
): ISelectedExperimentsFileAction => ({
  type: SELECTED_EXPERIMENTS_FILE,
  payload: { id, file },
});

export const discardExperimentsList = (id: string): IActionString => ({
  type: DISCARD_EXPERIMENT_LIST,
  payload: id,
});

export const validateExperimentList = (id: string) => {
  return (
    dispatch: (action: ExperimentListActionTypes) => void,
    getState: () => IApplicationState
  ) => {
    const {
      experimentLists,
      projectPicker: { config },
    } = getState();

    // config shouldn't be undefined if we can upload an experiment
    if (config === undefined) {
      return;
    }

    const experimentList = _.find(
      experimentLists.experimentLists,
      (x) => x.id === id
    );
    if (!experimentList) {
      return;
    }
    dispatch({ type: VALIDATING_EXPERIMENTS_FILE, payload: experimentList.id });
    validateExperimentsFile(experimentList.file, config)
      .then((validation) => {
        if (validation.experimentErrors.length) {
          dispatch({
            type: EXPERIMENTS_FILE_VALIDATION_FAILED,
            payload: { id, errors: validation.experimentErrors },
          });
        } else {
          dispatch({
            type: EXPERIMENTS_FILE_VALIDATION_SUCCEEDED,
            payload: { id, results: validation.results },
          });
        }
      })
      .catch(() => {});
  };
};

const getFileHash = (file: File): Promise<string> => {
  return new Promise((resolve) => {
    const reader = new FileReader();
    reader.readAsText(file, 'UTF-8');
    reader.onload = (evt) => {
      resolve(
        crypto
          .createHash(hashingAlgorithm)
          .update(reader.result as string)
          .digest('hex')
      );
    };
  });
};

export const startUpload = (id: string): any => {
  return (
    dispatch: (action: any) => void,
    getState: () => { experimentLists: IExperimentListsState }
  ) => {
    const { experimentLists } = getState();
    const experimentList = _.find(
      experimentLists.experimentLists,
      (x) => x.id === id
    );
    if (!experimentList) {
      return;
    }
    dispatch({
      type: START_UPLOAD,
      payload: id,
    });

    getFileHash(experimentList.file)
      .then((hash) =>
        startUploadRequest({
          experimentListName: experimentList.file.name,
          experimentListHash: hash,
          experimentJson: experimentList.experiment as IExperimentResult,
          experimentList: experimentList.file,
        })
      )
      .then(() => {
        dispatch(discardExperimentsList(id));
        // Even though we make a request to get all uploads when we
        // upload a single new experiment, this is still a local
        // change to the meta information of the uploads from the
        // app's perspective.
        dispatch({ type: UPLOADS_LOCAL_CHANGE });
        dispatch(getUploads());
      })
      .catch(() => {});
  };
};
